import { Fragment, useEffect, useMemo, useState } from 'react';
import { ArrowRight, RotateCcw, Undo2 } from 'lucide-react';
import { PlayerScaffold, PlayerFooter } from '@/components/player/PlayerScaffold';
import { Question } from '@/components/quiz/Question';
import { OptionState, type OptionCss } from '@/components/quiz/types';
import type { EducateModule } from '@/hooks/useEducateModule';
import type { DecisionTreeContent, DecisionTreeNode, EducateInteraction } from '@/types/educate';
import { OPTION_LETTERS } from '@/constants/ui';
import {
  clearPlayerProgress,
  loadPlayerProgress,
  savePlayerProgress
} from '@/utils/playerProgress';

interface DecisionTreeSaved {
  path: Step[];
  currentNodeId: string;
  endText: string | null;
  picked: number | null;
}

const DECISION_OPTION_CSS: OptionCss = {
  base: 'flex w-full items-center gap-3 rounded-xl border px-5 py-4 text-left text-[15px] leading-relaxed transition disabled:cursor-default',
  letter: 'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
  state: {
    [OptionState.DEFAULT]: 'border-slate-200 bg-white text-slate-700 hover:border-brand-400',
    [OptionState.SELECTED]: 'border-brand-600 bg-brand-50 text-slate-900',
    [OptionState.MUTED]: 'border-slate-200 bg-white text-slate-400',
    [OptionState.CORRECT]: '',
    [OptionState.INCORRECT]: ''
  },
  letterState: {
    [OptionState.DEFAULT]: 'bg-brand-50 text-brand-700',
    [OptionState.SELECTED]: 'bg-brand-600 text-white',
    [OptionState.MUTED]: 'bg-slate-100 text-slate-400',
    [OptionState.CORRECT]: '',
    [OptionState.INCORRECT]: ''
  }
};

interface Step {
  nodeId: string;
  choiceIndex: number;
}

export function DecisionTreePlayer({ module }: { module: EducateModule }) {
  const { storyline, completeInPlace, finishInPlace } = module;
  const content = storyline?.content as DecisionTreeContent | undefined;
  const showLogo = storyline?.uiContent.uiConfig?.showKnolskapeLogo ?? true;

  const nodes = content?.nodes ?? [];
  const normalizeId = (id: string | null | undefined) => (id ?? '').trim().toLowerCase();
  const resolveNode = (id: string | null | undefined): DecisionTreeNode | undefined => {
    if (id === null || id === undefined) return undefined;
    const exactMatch = nodes.find((candidate) => candidate.id === id);
    if (exactMatch) return exactMatch;
    const wanted = normalizeId(id);
    return nodes.find((candidate) => normalizeId(candidate.id) === wanted);
  };
  const canonicalId = (id: string | null | undefined) => resolveNode(id)?.id ?? '';
  const rootId = canonicalId(content?.startNodeId) || nodes[0]?.id || '';

  // Number nodes by their position in the source `nodes` array — the SAME scheme the editor
  // uses — so the player's labels match the editor's "Decision N" exactly.
  const nodeNumber = (id: string | null | undefined): number => {
    const resolved = resolveNode(id);
    if (!resolved) return 0;
    const index = nodes.findIndex((candidate) => candidate.id === resolved.id);
    return index >= 0 ? index + 1 : 0;
  };

  const saved = useMemo(
    () => loadPlayerProgress<DecisionTreeSaved>(storyline?.storylineId),
    [storyline?.storylineId]
  );
  const savedNodeId =
    typeof saved.currentNodeId === 'string' && resolveNode(saved.currentNodeId)
      ? canonicalId(saved.currentNodeId)
      : rootId;

  const [path, setPath] = useState<Step[]>(() => (Array.isArray(saved.path) ? saved.path : []));
  const [currentNodeId, setCurrentNodeId] = useState<string>(savedNodeId);
  const [endText, setEndText] = useState<string | null>(() =>
    typeof saved.endText === 'string' ? saved.endText : null
  );
  const [picked, setPicked] = useState<number | null>(() =>
    typeof saved.picked === 'number' ? saved.picked : null
  );

  useEffect(() => {
    savePlayerProgress<DecisionTreeSaved>(storyline?.storylineId, {
      path,
      currentNodeId,
      endText,
      picked
    });
  }, [storyline?.storylineId, path, currentNodeId, endText, picked]);

  const node: DecisionTreeNode | undefined = resolveNode(currentNodeId);
  const choices = node?.choices ?? [];
  const isTerminal = endText !== null || Boolean(node && (choices.length === 0 || node.terminal));
  const outcomeText = endText ?? node?.outcome ?? '';

  useEffect(() => {
    if (!isTerminal) return;
    const completedUnits = [...path.map((step) => `node-${step.nodeId}`), `node-${currentNodeId}`];
    const interactions: EducateInteraction[] = path.map((step) => ({
      unitId: `node-${step.nodeId}`,
      kind: 'choice',
      answer: resolveNode(step.nodeId)?.choices?.[step.choiceIndex]?.label ?? '',
      isCorrect: null
    }));
    completeInPlace([...new Set(completedUnits)], interactions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTerminal, currentNodeId, endText]);

  if (!content || !node) return null;

  const decisionNumber = nodeNumber(currentNodeId);

  const commit = () => {
    if (picked === null) return;
    const choice = choices[picked];
    const committedNodeId = node?.id ?? currentNodeId;
    setPath((prev) => [...prev, { nodeId: committedNodeId, choiceIndex: picked }]);
    setPicked(null);
    const nextNode = resolveNode(choice.nextNodeId);
    if (nextNode) setCurrentNodeId(nextNode.id);
    else setEndText(choice.consequence);
  };

  const rethink = () => {
    if (picked !== null) {
      setPicked(null);
      return;
    }
    setEndText(null);
    if (path.length > 0) {
      const previous = path[path.length - 1];
      setPath((prev) => prev.slice(0, -1));
      setCurrentNodeId(previous.nodeId);
    }
  };

  const jumpTo = (stepIndex: number) => {
    setEndText(null);
    setPicked(null);
    setCurrentNodeId(path[stepIndex].nodeId);
    setPath((prev) => prev.slice(0, stepIndex));
  };

  const startOver = () => {
    clearPlayerProgress(storyline?.storylineId);
    setPath([]);
    setEndText(null);
    setPicked(null);
    setCurrentNodeId(rootId);
  };

  const canRethink = picked !== null || path.length > 0;

  return (
    <PlayerScaffold
      eyebrow="Decision Tree"
      title={storyline?.uiContent.title ?? ''}
      showLogo={showLogo}
      setting={content.setting}
      cast={content.cast}
      bodyTestId="decision-screen"
      nav={
        <DecisionBreadcrumb
          path={path}
          nodes={nodes}
          atOutcome={isTerminal}
          current={decisionNumber}
          numberFor={nodeNumber}
          onJump={jumpTo}
        />
      }
      footer={
        <PlayerFooter
          left={
            <>
              <button
                type="button"
                onClick={rethink}
                disabled={!canRethink}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:opacity-40"
              >
                <Undo2 size={15} /> Rethink
              </button>
              <button
                type="button"
                onClick={startOver}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400"
              >
                <RotateCcw size={15} /> Start over
              </button>
            </>
          }
          center={
            isTerminal
              ? `${path.length} ${path.length === 1 ? 'decision' : 'decisions'} made · path complete`
              : picked === null
                ? 'Pick an option to see what happens'
                : `Decision ${decisionNumber} made · read the consequence`
          }
          right={
            isTerminal ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={startOver}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400"
                >
                  Explore another path
                </button>
                <button
                  type="button"
                  onClick={finishInPlace}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-700"
                >
                  Finish
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={commit}
                disabled={picked === null}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-40"
              >
                Continue <ArrowRight size={16} />
              </button>
            )
          }
        />
      }
    >
      {isTerminal ? (
        <Outcome title={node.title} text={outcomeText} />
      ) : (
        <DecisionPoint node={node} number={decisionNumber} picked={picked} onPick={setPicked} />
      )}
    </PlayerScaffold>
  );
}

function DecisionPoint({
  node,
  number,
  picked,
  onPick
}: {
  node: DecisionTreeNode;
  number: number;
  picked: number | null;
  onPick: (index: number) => void;
}) {
  const choices = node.choices ?? [];
  const chosen = picked !== null ? choices[picked] : null;

  const optionState = (index: number): OptionState =>
    picked === null
      ? OptionState.DEFAULT
      : index === picked
        ? OptionState.SELECTED
        : OptionState.MUTED;

  return (
    <article>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-600">
        Decision point {number} · {choices.length} options
      </p>
      <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-brand-600">
        What do you do?
      </p>
      <h2 className="mt-2 text-2xl font-bold text-slate-900">
        {node.title ?? `Decision ${number}`}
      </h2>

      <div className="mt-5 rounded-xl border-l-4 border-amber-400 bg-white p-6 shadow-sm">
        <p className="whitespace-pre-line text-[15px] leading-relaxed text-slate-700">
          {node.situation}
        </p>

        <Question
          question=""
          options={choices.map((choice) => choice.label)}
          optionState={optionState}
          onSelect={(index) => picked === null && onPick(index)}
          isDisabled={() => picked !== null}
          title={<></>}
          css={{ container: 'mt-5', list: 'space-y-3', option: DECISION_OPTION_CSS }}
        />
      </div>

      {chosen && (
        <div className="mt-5 rounded-xl border-l-4 border-brand-500 bg-white p-6 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-600">
            What happens next
          </p>
          <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-slate-700">
            {chosen.consequence}
          </p>
        </div>
      )}
    </article>
  );
}

function Outcome({ title, text }: { title?: string; text: string }) {
  return (
    <article>
      <div className="rounded-xl border-l-4 border-brand-500 bg-white p-6 shadow-sm">
        <span className="inline-block rounded-full bg-brand-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-700">
          The outcome
        </span>
        <h2 className="mt-3 text-2xl font-bold text-slate-900">
          {title ?? 'How this path played out'}
        </h2>
        <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-slate-700">
          {text}
        </p>
      </div>
    </article>
  );
}

function DecisionBreadcrumb({
  path,
  nodes,
  atOutcome,
  current,
  numberFor,
  onJump
}: {
  path: Step[];
  nodes: DecisionTreeNode[];
  atOutcome: boolean;
  current: number;
  numberFor: (id: string | null | undefined) => number;
  onJump: (stepIndex: number) => void;
}) {
  const normalizeId = (id: string | null | undefined) => (id ?? '').trim().toLowerCase();
  const resolveNode = (id: string) =>
    nodes.find((candidate) => candidate.id === id) ??
    nodes.find((candidate) => normalizeId(candidate.id) === normalizeId(id));

  return (
    <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
      {path.map((step, index) => {
        const letter = OPTION_LETTERS[step.choiceIndex] ?? step.choiceIndex + 1;
        return (
          <Fragment key={`crumb-${index}-${step.nodeId}`}>
            {index > 0 && <span className="px-1 text-slate-300">→</span>}
            <button
              type="button"
              onClick={() => onJump(index)}
              title={resolveNode(step.nodeId)?.title ?? undefined}
              className="rounded-full bg-brand-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-brand-700 transition hover:bg-brand-100"
            >
              Decision {numberFor(step.nodeId)} · {letter}
            </button>
          </Fragment>
        );
      })}

      {path.length > 0 && <span className="px-1 text-slate-300">→</span>}

      <span
        className={`rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide ${
          atOutcome ? 'bg-slate-100 text-slate-500' : 'bg-brand-600 text-white'
        }`}
      >
        {atOutcome ? 'Outcome' : `Decision ${current}`}
      </span>
    </div>
  );
}
